// YearBillGrid — grid tahunan bil bulanan untuk portal orangtua (ala rujukan kabarwarga).
// Ibu bapa nampak status 12 bulan sepintas lalu, ketik beberapa bulan, bayar sekali gus
// dengan satu transfer + satu bukti. Props:
//   student: { id, nama, kelas }
//   bills: [{ id, tarifNama, periode ('YYYY-MM' bulanan), diskon_nota, due_date, status, baki, jumlah_bayar_rm, ... }]
//   proofs: [{ id, invoice_id, status, ... }] (untuk pelajar ini)
//   pendingByInvoice: { invoiceId: true } — invois terpaut bukti pending via proof_invoices
//   school: { no_akaun_bank, qr_bank_url, telefon } (maklumat bank + WhatsApp bendahara)
//   onUploaded: callback selepas bukti berjaya dimuat naik (untuk refresh data)
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check, MessageCircle, Upload, Loader2,
  AlertTriangle, CheckCircle2, Building2, QrCode, FileText, Banknote,
} from 'lucide-react';
import { formatRM } from '../lib/format';
import { waLink as buildWaLink } from '../lib/wa';
import { uploadBukti } from '../lib/drive';
import { CenterMorphModal, CenterMorphModalContent } from './beui/CenterMorphModal';
import { Button } from './ui/button';

const klNow = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }));
const klDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const CELL = {
  kosong: 'border-border bg-muted text-muted-foreground cursor-default',
  belum: 'border-border bg-card text-muted-foreground hover:border-brand/60 hover:shadow-sm',
  separa: 'border-sky-300 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 hover:border-sky-500 hover:shadow-sm',
  dicek: 'border-amber-300 dark:border-amber-700 border-dashed bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 cursor-default',
  lunas: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 cursor-default',
};
const SEL_RING = 'ring-2 ring-brand border-brand';

export default function YearBillGrid({ student, bills, proofs, pendingByInvoice, school, onUploaded }) {
  const { t, i18n } = useTranslation();
  const now = useMemo(() => klNow(), []);
  const todayStr = useMemo(() => klDateStr(now), [now]);
  const curMonth = now.getMonth() + 1;
  const curYear = now.getFullYear();

  const locale = i18n.language === 'en' ? 'en-MY' : i18n.language === 'id' ? 'id-ID' : 'ms-MY';
  const monthName = (m, y) => new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(y || curYear, m - 1, 1));

  // Tahun tersedia: tahun-tahun yang ada bil + tahun lalu & tahun depan (ala rujukan —
  // ibu bapa boleh lihat tunggakan tahun lalu walaupun tahun itu tiada bil aktif)
  const availYears = useMemo(() => {
    const s = new Set([curYear - 1, curYear, curYear + 1]);
    (bills || []).forEach((b) => {
      const m = b.periode && String(b.periode).match(/^(\d{4})/);
      if (m) s.add(parseInt(m[1], 10));
    });
    return [...s].sort((a, b2) => a - b2);
  }, [bills, curYear]);

  const [year, setYear] = useState(curYear);
  useEffect(() => {
    setYear((y) => (availYears.includes(y) ? y : Math.max(...availYears)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills]);

  const [selected, setSelected] = useState([]); // invoice ids
  const [payOpen, setPayOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState('');
  const [upOk, setUpOk] = useState(false);

  // Invois bulanan tahun terpilih: bulan (1-12) -> [bills]
  const monthly = useMemo(() => {
    const map = {};
    (bills || []).forEach((b) => {
      const mt = b.periode && String(b.periode).match(new RegExp(`^${year}-(0[1-9]|1[0-2])$`));
      if (!mt) return;
      const m = parseInt(mt[1], 10);
      (map[m] = map[m] || []).push(b);
    });
    return map;
  }, [bills, year]);

  // Bil bukan bulanan (tahunan 'YYYY' dsb.) — kekal senarai biasa di bawah grid
  const others = useMemo(
    () => (bills || []).filter((b) => !(b.periode && /^\d{4}-(0[1-9]|1[0-2])$/.test(String(b.periode)))),
    [bills],
  );

  const pendingSet = useMemo(() => {
    const s = new Set();
    (proofs || []).filter((p) => p.status === 'pending').forEach((p) => { if (p.invoice_id) s.add(p.invoice_id); });
    Object.keys(pendingByInvoice || {}).forEach((k) => s.add(k));
    return s;
  }, [proofs, pendingByInvoice]);

  const paidOf = (b) => Math.max(0, Number(b.jumlah_bayar_rm || 0) - Number(b.baki || 0));

  const monthState = (m) => {
    const invs = monthly[m] || [];
    if (!invs.length) return 'kosong';
    if (invs.some((b) => pendingSet.has(b.id))) return 'dicek';
    if (invs.every((b) => Number(b.baki || 0) <= 0)) return 'lunas';
    if (invs.some((b) => paidOf(b) > 0)) return 'separa';
    return 'belum';
  };

  const monthOverdue = (m) => (monthly[m] || []).some(
    (b) => Number(b.baki || 0) > 0 && b.due_date && b.due_date < todayStr,
  );

  const unpaidIds = (m) => (monthly[m] || []).filter((b) => Number(b.baki || 0) > 0 && !pendingSet.has(b.id)).map((b) => b.id);

  const toggleMonth = (m) => {
    const ids = unpaidIds(m);
    if (!ids.length) return;
    setSelected((sel) => {
      const has = new Set(sel);
      const allIn = ids.every((id) => has.has(id));
      return allIn ? sel.filter((id) => !ids.includes(id)) : [...sel, ...ids.filter((id) => !has.has(id))];
    });
  };

  const selBills = useMemo(
    () => selected.map((id) => (bills || []).find((b) => b.id === id)).filter(Boolean),
    [selected, bills],
  );
  const selMonths = useMemo(() => {
    const s = new Set();
    selBills.forEach((b) => {
      const mt = b.periode && String(b.periode).match(/^(\d{4})-(0[1-9]|1[0-2])$/);
      s.add(mt ? `${mt[1]}-${mt[2]}` : b.id);
    });
    return s.size;
  }, [selBills]);
  const selTotal = useMemo(() => selBills.reduce((a, b) => a + Number(b.baki || 0), 0), [selBills]);

  const paidMonths = useMemo(() => {
    let n = 0;
    for (let m = 1; m <= 12; m += 1) if (monthState(m) === 'lunas') n += 1;
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthly, pendingSet, year]);

  // Lencana bulan semasa (seperti rujukan: "Lunas Okt" / "Dicek Okt")
  const curBadge = year === curYear ? monthState(curMonth) : null;

  const openPay = (ids) => {
    setSelected(ids);
    setFile(null); setUpErr(''); setUpOk(false);
    setPayOpen(true);
  };
  const closePay = () => {
    setPayOpen(false);
    setFile(null); setUpErr(''); setUpOk(false);
    setSelected([]);
  };

  const doUpload = async () => {
    if (!file || !selBills.length) return;
    setUploading(true); setUpErr('');
    try {
      await uploadBukti({ file, studentId: student.id, invoiceIds: selBills.map((b) => b.id) });
      setUpOk(true);
      if (onUploaded) onUploaded();
    } catch (e) { setUpErr(e.message); }
    setUploading(false);
  };

  const waLink = buildWaLink(school?.telefon, t('waTanya', { nama: student.nama }));

  const monLabel = (b) => {
    const mt = b.periode && String(b.periode).match(/^(\d{4})-(0[1-9]|1[0-2])$/);
    return mt ? `${monthName(parseInt(mt[2], 10), parseInt(mt[1], 10))} ${mt[1]}` : (b.periode || '');
  };

  // Tukar tahun: reset pilihan bulan (pilihan hanya relevan untuk tahun dipapar)
  const changeYear = (y) => {
    setYear(y);
    setSelected([]);
  };

  return (
    <div>
      {/* Header status tahun */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground">
          {t('statusTahun', { year })}:{' '}
          <span className="font-extrabold text-foreground">{t('bulanLunas', { n: paidMonths })}</span>
        </p>
        {curBadge === 'lunas' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold px-2.5 py-1">
            <Check className="h-3 w-3" />{t('lunasBulan', { mon: monthName(curMonth, year) })}
          </span>
        )}
        {curBadge === 'dicek' && (
          <span className="inline-flex items-center rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 text-[11px] font-bold px-2.5 py-1">
            {t('dicekBulan', { mon: monthName(curMonth, year) })}
          </span>
        )}
      </div>

      {/* Navigasi tahun — tab eksplisit ala rujukan (tahun lalu / ini / depan) */}
      <div className="flex items-center justify-center gap-1.5 mb-2 flex-wrap">
        {availYears.map((y) => (
          <button key={y} type="button" onClick={() => changeYear(y)}
            className={`h-8 min-w-[3.5rem] px-3 rounded-lg text-sm font-bold transition-all ${
              y === year ? 'bg-slate-900 text-white shadow-sm dark:bg-slate-200 dark:text-slate-900' : 'text-muted-foreground hover:bg-muted'
            }`}>
            {y}
          </button>
        ))}
      </div>

      {/* Grid 12 bulan */}
      <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => {
          const st = monthState(m);
          const selectable = st === 'belum' || st === 'separa';
          const ids = selectable ? unpaidIds(m) : [];
          const isSel = selectable && ids.length > 0 && ids.every((id) => selected.includes(id));
          const overdue = monthOverdue(m);
          return (
            <button key={m} type="button" disabled={!selectable} onClick={() => toggleMonth(m)}
              className={`relative rounded-lg border px-1 py-1.5 text-center transition-all ${CELL[st]} ${isSel ? SEL_RING : ''} ${selectable ? 'cursor-pointer' : ''}`}>
              <span className="block text-[10px] sm:text-[11px] font-semibold leading-tight">{monthName(m, year)}</span>
              <span className="block text-xs sm:text-sm font-bold leading-tight mt-0.5">
                {st === 'lunas' && <Check className="h-3.5 w-3.5 mx-auto" />}
                {st === 'dicek' && <span className="tracking-widest">…</span>}
                {st === 'separa' && <span>{formatRM((monthly[m] || []).reduce((a, b) => a + Number(b.baki || 0), 0))}</span>}
                {(st === 'belum' || st === 'kosong') && <span className="opacity-60">–</span>}
              </span>
              {overdue && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500" title={t('lewatHari', { n: '' }).trim()} />}
              {isSel && (
                <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-brand text-white flex items-center justify-center">
                  <Check className="h-2.5 w-2.5" />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1.5">{t('pilihBulan')}</p>

      {/* Tindakan */}
      <div className="mt-2 flex gap-2">
        <Button onClick={() => openPay(selected)} disabled={!selBills.length}
          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-sm font-bold disabled:opacity-40">
          <Banknote className="h-4 w-4 mr-1.5" />
          {selBills.length ? `${t('bayarBulan', { n: selMonths })} · ${formatRM(selTotal)}` : t('bayar')}
        </Button>
        {waLink && (
          <a href={waLink} target="_blank" rel="noreferrer"
            className="h-11 px-4 inline-flex items-center gap-1.5 rounded-xl border border-border bg-card text-sm font-semibold text-foreground hover:bg-muted shrink-0">
            <MessageCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />{t('tanyaBendahara')}
          </a>
        )}
      </div>

      {/* Bil bukan bulanan */}
      {others.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-bold text-muted-foreground mb-1.5">{t('lainLain')}</p>
          <div className="space-y-1.5">
            {others.map((b) => {
              const isPending = pendingSet.has(b.id);
              const lunas = Number(b.baki || 0) <= 0;
              return (
                <div key={b.id} className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-foreground truncate">{b.tarifNama}{b.diskon_nota ? ` (${b.diskon_nota})` : ''}</p>
                    <p className="text-[11px] text-muted-foreground">{b.periode || ''}</p>
                  </div>
                  {lunas ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400"><Check className="h-3.5 w-3.5" />{t('stLunas')}</span>
                  ) : isPending ? (
                    <span className="text-[11px] font-bold text-amber-600 dark:text-amber-400">{t('dicekBulan', { mon: '' }).trim()}</span>
                  ) : (
                    <>
                      <span className="text-xs font-extrabold text-foreground font-mono tabular">{formatRM(b.baki)}</span>
                      <Button size="sm" onClick={() => openPay([b.id])} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs font-bold">{t('bayar')}</Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Dialog bayar multi-bulan */}
      <CenterMorphModal open={payOpen} onOpenChange={(o) => !o && closePay()}>
        <CenterMorphModalContent ariaLabel={t('bayar')} className="max-w-md bg-card">
          <div className="p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">
                {selMonths > 1 ? t('bayarBulan', { n: selMonths }) : `${t('bayar')} — ${student.nama}`}
              </h2>
            </div>
            <div className="space-y-4">
              <div className="rounded-xl bg-muted p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground text-center">{t('jumlahPerluDibayar')}</p>
                <p className="text-3xl font-black font-mono tabular text-foreground text-center">{formatRM(selTotal)}</p>
                <div className="mt-2 space-y-1">
                  {selBills.map((b) => (
                    <div key={b.id} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground truncate">{b.tarifNama} · {monLabel(b)}{b.diskon_nota ? ` (${b.diskon_nota})` : ''}</span>
                      <span className="font-bold font-mono tabular text-foreground shrink-0 ml-2">{formatRM(b.baki)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-border p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{t('pindahanBank')}</p>
                {school?.no_akaun_bank ? (
                  <p className="font-mono font-bold text-foreground">{school.no_akaun_bank}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('tiadaMaklumatBank')}</p>
                )}
                {school?.qr_bank_url && (
                  <div className="mt-3 flex flex-col items-center gap-1">
                    <QrCode className="h-4 w-4 text-muted-foreground" />
                    <img src={school.qr_bank_url} alt="QR bank" className="w-40 h-40 object-contain rounded-lg border border-border" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">{t('arahanBayar')}</p>
              </div>

              {upOk ? (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />{t('buktiDihantar')}
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">{t('muatNaikBukti')}</label>
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setUpErr(''); }}
                    className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:h-10 file:px-4 file:rounded-xl file:border-0 file:bg-brand file:text-white file:text-sm file:font-semibold hover:file:bg-brand-royal" />
                  <p className="text-[11px] text-muted-foreground mt-1">{t('hadFail')}</p>
                  {file && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{file.name}</p>}
                  {upErr && <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{upErr}</p>}
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={closePay} className="flex-1 h-11">{t('tutup')}</Button>
              {!upOk && (
                <Button onClick={doUpload} disabled={uploading || !file || !selBills.length} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  {t('hantarBukti')}
                </Button>
              )}
            </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>
    </div>
  );
}
